/**
 * i18n Configuration
 *
 * Internationalization setup using i18next with Czech as the default language
 * and English as secondary language.
 *
 * Czech Pluralization Rules:
 * Czech has complex plural forms that differ from English:
 * - 1 (one): "1 položka" (singular)
 * - 2-4 (few): "2 položky" (few)
 * - 0, 5+ (other/many): "5 položek" (many)
 *
 * i18next uses the following plural suffixes for Czech:
 * - _one: count === 1
 * - _few: count >= 2 && count <= 4
 * - _other: count === 0 || count >= 5
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import cs from './locales/cs.json';
import en from './locales/en.json';

/**
 * Default language (Czech)
 */
export const defaultLanguage = 'cs' as const;

/**
 * Supported languages
 */
export const supportedLanguages = ['cs', 'en'] as const;

/**
 * Type for supported language codes
 */
export type SupportedLanguage = (typeof supportedLanguages)[number];

/**
 * Language display names
 */
export const languageNames: Record<SupportedLanguage, string> = {
  cs: 'Čeština',
  en: 'English',
};

/**
 * Language flags (emoji)
 */
export const languageFlags: Record<SupportedLanguage, string> = {
  cs: '🇨🇿',
  en: '🇬🇧',
};

/**
 * Initialize i18next
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      cs: { translation: cs },
      en: { translation: en },
    },
    fallbackLng: defaultLanguage,
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
    },
    // Configure pluralization rules
    pluralSeparator: '_',
    // Custom plural rules for Czech
    // i18next v23+ uses Intl.PluralRules by default which handles Czech correctly
    // but we can also configure it explicitly for clarity
  });

// Note: Czech pluralization is handled automatically by modern i18next (v21+)
// which uses Intl.PluralRules. The plural categories for Czech are: one, few, many, other
// i18next maps these to: one, few, other (combining many into other)
// We use _one, _few, _other suffixes in our translation files

export default i18n;
